import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MapCoords, MapLocation} from '@shared/models/map.model';
import {Observable} from 'rxjs';
import {environment} from '@environments/environment';
import {AppConfigService} from '@services/app-config.service';
import {configVariableValues} from '@constants/config-variable-values.constants';
import {ToastService} from '@core/src/public-api';
import {UserProfileAddress} from '@shared/models/user-profile.model';
import {Address} from 'ngx-google-places-autocomplete/objects/address';
import {AddressComponent} from 'ngx-google-places-autocomplete/objects/addressComponent';
import {Options} from 'ngx-google-places-autocomplete/objects/options/options';

const GOOGLE_MAPS_SCRIPT_URL = 'https://maps.googleapis.com/maps/api/js?&libraries=places&language=es&key=';
declare const google: any;

@Injectable({
  providedIn: 'root'
})
export class MapsService {
  scriptUrl: string;
  autocompleteService: any;
  placesService: any;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private toastService: ToastService
  ) {
  }

  async initLibrary(): Promise<void> {
    const isMapsEnabled = this.appConfigService.hasConfigurationEnabled(configVariableValues.GOOGLE_MAPS);
    if (!isMapsEnabled || !environment.google.mapsApiKey) {
      throw new Error();
    }

    this.scriptUrl = GOOGLE_MAPS_SCRIPT_URL + environment.google.mapsApiKey;
    return this.loadScript();
  }

  loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        if (document.querySelector(`script[src*="${this.scriptUrl}"]`)) {
          resolve();
        } else {
          const script = document.createElement('script');
          script.src = this.scriptUrl;
          script.async = true;
          script.defer = true;
          script.onload = (a) => {
            this.autocompleteService = new google.maps.places.AutocompleteService();
            this.placesService = new google.maps.places.PlacesService(
              document.createElement('div')
            );
            resolve();
          }
          script.onerror = () => {
            const error = 'Failed to load MAPS script';
            console.error(error);
            reject(new Error(error));
          };
          document.head.appendChild(script);
        }
      } catch (error) {
        console.error(error);
      }
    });
  }

  async getFirstPrediction(text: string, options: any): Promise<Address> {
    const placePredictions = await this.getPlacePredictions(text, options);
    if (!placePredictions?.length) {
      return;
    }

    const placeDetails = await this.getPlaceDetails(placePredictions[0].place_id);
    if (!placeDetails) {
      return;
    }

    return placeDetails;
  }

  getPlacePredictions(text: string, options: Options): Promise<Array<any>> {
    return new Promise((resolve, reject) => {
      this.autocompleteService.getPlacePredictions({input: text, ...options}, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(predictions);
        } else {
          const error = 'Failed to getPlacePredictions';
          console.error(error);
          reject(new Error(error));
        }
      },
        err => {
          reject(err);
        }
      );
    });
  }

  getPlaceDetails(placeId: string): Promise<Address> {
    return new Promise((resolve, reject) => {
      this.placesService.getDetails({placeId: placeId}, (place: Address, detailStatus: string) => {
        if (detailStatus === google.maps.places.PlacesServiceStatus.OK) {
          resolve(place);
        } else {
          const error = 'Failed to getPlaceDetails';
          console.error(error);
          reject(new Error(error));
        }
      },
        err => {
          reject(err);
        }
      );
    });
  }

  isGeolocationAvailable(): boolean {
    return !!navigator?.geolocation;
  }

  getPosition(): Promise<google.maps.LatLngLiteral> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resp => {
        resolve({lng: resp.coords.longitude, lat: resp.coords.latitude});
      },
        err => {
          this.toastService.warning('SI_USE_LOCATION_CHECK_PERMISSION');
          reject(err);
        });
    });
  }

  getLocation(latitude: number, longitude: number): Observable<MapLocation> {
    return this.http.get<MapLocation>(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${environment.google.mapsApiKey}`, {responseType: 'json'}
    );
  }

  getCoords(address: string): Observable<MapCoords> {
    return this.http.get<MapCoords>(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${address}&sensor=false&key=${environment.google.mapsApiKey}`, {responseType: 'json'}
    );
  }

  openGoogleMaps(address: string): void {
    const link = `https://www.google.com/maps/search/?api=1&query=${address}`;
    window.open(link, '_blank');
  }

  disassembleAddress(address: Address): UserProfileAddress {
    const model = new UserProfileAddress();
    model.addressModel = address;
    model.formattedAddress = address?.formatted_address;
    if (address.address_components) {
      model.street = this.findAdressObjectWithTypes(address, ['route'])?.long_name;
      model.streetNumber = this.findAdressObjectWithTypes(address, ['street_number'])?.long_name;
      model.postalCode = this.findAdressObjectWithTypes(address, ['postal_code'])?.long_name;
      model.city = this.findAdressObjectWithTypes(address, ['locality'])?.long_name;
      model.state = this.findAdressObjectWithTypes(address, ['administrative_area_level_1'])?.long_name;
      model.district = this.findAdressObjectWithTypes(address, ['administrative_area_level_2'])?.long_name;
      model.country = {
        id: null,
        name: this.findAdressObjectWithTypes(address, ['country'])?.long_name,
        code: this.findAdressObjectWithTypes(address, ['country'])?.short_name
      };
    }

    return model;
  }

  findAdressObjectWithTypes(address: Address, types: string[]): AddressComponent {
    return address.address_components.find(component =>
      types.every(type => component.types.includes(type))
    );
  }
}
